import { useState, useContext } from 'react';
import { Grid, Typography } from '@mui/material';
import {
    CameraAltOutlined,
    CheckCircleOutline,
    OpenInNewOutlined,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttSportSelector,
    SttFormTime,
    SttFormPeriod,
    SttFormCheck,
    SttButtonSave,
    SttButtonDelete,
    SttButton,
    SttLabelForm,
    SttButtonRecover,
    SttSelectTeacher,
    FieldHelpText,
} from '../../../sporttia/all';
import AttendancesDialog from './AttendancesDialog';
import { useCrud } from '../../../lib/hooks';
import SttTagsList from '../../../sporttia/lists/SttTagsList';
import { getObjectDiff, parseToDate, safeFormatDate } from '../../../lib/utils';
import { isRenewalPreenrollmentVisible } from '../../../config/features';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type ActivityLocal = {
    id?: number;
    created?: string;
    name?: string;
    description?: string;
    status?: string | null;
    type?: string | null;
    sport?: {
        id: number;
        name: string;
    } | null;
    teacher?: {
        id: number | null;
        name?: string;
    } | null;
    maxStudents?: number;
    inscIni?: string;
    inscEnd?: string;
    ini?: string;
    end?: string;
    showSignedUsers?: boolean;
    minAge?: number | null;
    maxAge?: number | null;
    tags?: unknown[];
    trash?: boolean;
    preenrollmentAllowed?: boolean;
    preenrollmentIni?: string | null;
    preenrollmentEnd?: string | null;
    originalClass?: { id: number; name?: string } | null;
    relocateStudents?: boolean;
};

/** Time used when a pre-enrollment date is picked while its field was still empty. */
const PREENROLLMENT_DEFAULT_TIME: Record<string, string> = {
    preenrollmentIni: '00:00:00',
    preenrollmentEnd: '23:59:00',
};

type TeacherLocal = {
    id: number;
    name: string;
    fullName: string;
};

type SportLocal = {
    id: number;
    name: string;
};

type Props = {
    item: ActivityLocal;
    teachers?: TeacherLocal[];
    onSave: (activity: ActivityLocal) => void;
    onDelete: (activity: ActivityLocal) => void;
};

export default function ActivityDetails({
    item,
    teachers = [],
    onSave,
    onDelete,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [activity, setActivity, setPropertyOriginal, , Post, Put, Delete] =
        useCrud<ActivityLocal>(item);
    const [currentActivityState, setCurrentActivityState] =
        useState<ActivityLocal>(activity);
    const [dialog, setDialog] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Wrapper function to adapt setProperty to form component expectations
    function setProperty({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        setPropertyOriginal({
            name: name as keyof ActivityLocal,
            value: value as ActivityLocal[keyof ActivityLocal],
        });
    }

    function setInscDate(name: string, date: string): void {
        const parsedDate = parseISO(date);
        if (!isValid(parsedDate)) {
            return;
        }

        const currentValue = activity[name as keyof ActivityLocal] as string;
        const currentDate = parseISO(currentValue);

        setPropertyOriginal({
            name: name as keyof ActivityLocal,
            value: `${format(parsedDate, 'yyyy-MM-dd')} ${format(
                currentDate,
                'HH:mm:ss',
            )}` as ActivityLocal[keyof ActivityLocal],
        });
    }

    function updateActivityTags(tags: unknown[]): void {
        setActivity({ ...activity, tags });
    }

    function setInscTime(name: string, time: string): void {
        const currentValue = activity[name as keyof ActivityLocal] as string;
        const currentDate = parseISO(currentValue);

        setPropertyOriginal({
            name: name as keyof ActivityLocal,
            value: `${format(currentDate, 'yyyy-MM-dd')} ${time}` as ActivityLocal[keyof ActivityLocal],
        });
    }

    /**
     * Sets the date half of a pre-enrollment bound, keeping the time already chosen.
     * Clearing the date clears the whole bound, which is how the window is turned off.
     */
    function setPreenrollmentDate(name: string, date: unknown): void {
        const parsedDate = parseToDate(date as string);

        if (!parsedDate) {
            setPropertyOriginal({
                name: name as keyof ActivityLocal,
                value: null as ActivityLocal[keyof ActivityLocal],
            });
            return;
        }

        const currentDate = parseToDate(
            activity[name as keyof ActivityLocal] as string,
        );
        const time = currentDate
            ? format(currentDate, 'HH:mm:ss')
            : (PREENROLLMENT_DEFAULT_TIME[name] ?? '00:00:00');

        setPropertyOriginal({
            name: name as keyof ActivityLocal,
            value: `${format(
                parsedDate,
                'yyyy-MM-dd',
            )} ${time}` as ActivityLocal[keyof ActivityLocal],
        });
    }

    /** Sets the time half of a pre-enrollment bound. Ignored while its date is empty. */
    function setPreenrollmentTime(name: string, time: string): void {
        const currentDate = parseToDate(
            activity[name as keyof ActivityLocal] as string,
        );

        if (!currentDate) {
            return;
        }

        setPropertyOriginal({
            name: name as keyof ActivityLocal,
            value: `${format(
                currentDate,
                'yyyy-MM-dd',
            )} ${time}` as ActivityLocal[keyof ActivityLocal],
        });
    }

    function setSport(sport: SportLocal): void {
        setPropertyOriginal({
            name: 'sport',
            value: { id: sport.id, name: sport.name },
        });
    }

    function setTeacher(teacherId: number | null): void {
        if (!teacherId) {
            setPropertyOriginal({
                name: 'teacher',
                value: {
                    id: null,
                },
            });
        } else {
            const teacher = teachers.find(
                (t: TeacherLocal) => t.id === teacherId,
            );

            setPropertyOriginal({
                name: 'teacher',
                value: {
                    id: teacher?.id,
                    name: teacher?.name,
                } as ActivityLocal['teacher'],
            });
        }
    }

    function saveActivity(): void {
        const preenrollmentIniDate = parseToDate(activity.preenrollmentIni);
        const preenrollmentEndDate = parseToDate(activity.preenrollmentEnd);

        if (
            preenrollmentIniDate &&
            preenrollmentEndDate &&
            preenrollmentIniDate >= preenrollmentEndDate
        ) {
            cxt?.showMessage('E', t('activity.preenrollment.invalidPeriod'));
            return;
        }

        const inscIniDate = parseToDate(activity.inscIni);
        const inscEndDate = parseToDate(activity.inscEnd);

        // FR-018: los dos periodos no pueden solaparse. Comparaciones estrictas
        // porque pegarse no es solaparse, igual que en el backend.
        if (
            preenrollmentIniDate &&
            preenrollmentEndDate &&
            inscIniDate &&
            inscEndDate &&
            preenrollmentIniDate < inscEndDate &&
            inscIniDate < preenrollmentEndDate
        ) {
            cxt?.showMessage('E', t('activity.preenrollment.overlapsSignup'));
            return;
        }

        setSaveLoading(true);

        if (activity.id) {
            const updatedActivityFields = getObjectDiff(
                activity,
                currentActivityState,
            );

            Put(`/activities/${activity.id}`, {
                name: updatedActivityFields.name,
                description: updatedActivityFields.description,
                status: updatedActivityFields.status,
                type: updatedActivityFields.type,
                idSport: updatedActivityFields.sport?.id,
                idTeacher: updatedActivityFields.teacher?.id,
                maxStudents: updatedActivityFields.maxStudents,
                inscIni: updatedActivityFields.inscIni,
                inscEnd: updatedActivityFields.inscEnd,
                ini: updatedActivityFields.ini,
                end: updatedActivityFields.end,
                showSignedUsers: updatedActivityFields.showSignedUsers,
                minAge: updatedActivityFields.minAge,
                maxAge: updatedActivityFields.maxAge,
                preenrollmentIni: updatedActivityFields.preenrollmentIni,
                preenrollmentEnd: updatedActivityFields.preenrollmentEnd,
            })
                .then((response: unknown) => {
                    const typedResponse = response as {
                        activity?: ActivityLocal;
                    };
                    if (typedResponse.activity) {
                        setActivity(typedResponse.activity);
                        setCurrentActivityState(typedResponse.activity);

                        onSave(typedResponse.activity);
                    }
                    setSaveLoading(false);
                })
                .catch(() => {
                    setActivity(currentActivityState);
                    setSaveLoading(false);
                })
                .finally(() => {
                    setSaveLoading(false);
                });
        } else {
            const newActivity: Partial<ActivityLocal> & {
                idSport?: number | null;
                idTeacher?: number | null;
            } = {
                name: activity.name,
                description: activity.description,
                status: activity.status,
                type: activity.type,
                idSport: activity.sport ? activity.sport.id : null,
                maxStudents: activity.maxStudents,
                inscIni: activity.inscIni,
                inscEnd: activity.inscEnd,
                ini: activity.ini,
                end: activity.end,
                showSignedUsers: activity.showSignedUsers,
                minAge: activity.minAge,
                maxAge: activity.maxAge,
            };

            if (activity?.teacher) {
                newActivity.idTeacher = activity.teacher.id;
            }

            Post(`/activities`, newActivity)
                .then((response: unknown) => {
                    const typedResponse = response as {
                        activity?: ActivityLocal;
                    };
                    if (typedResponse.activity) {
                        setActivity(typedResponse.activity);
                        onSave(typedResponse.activity);
                    }
                    setSaveLoading(false);
                })
                .catch(() => {
                    setSaveLoading(false);
                })
                .finally(() => {
                    setSaveLoading(false);
                });
        }
    }

    function deleteActivity(): void {
        setDeleteLoading(true);
        Delete(`/activities/${activity.id}`)
            .then((response: unknown) => {
                const typedResponse = response as { activity?: ActivityLocal };
                if (typedResponse.activity) {
                    setActivity({ ...activity, trash: true });
                    onDelete(typedResponse.activity);
                }
                setDeleteLoading(false);
            })
            .catch(() => {
                setDeleteLoading(false);
            })
            .finally(() => {
                setDeleteLoading(false);
            });
    }

    function duplicate(): void {
        Post(`/activities/${activity.id}/duplicate`)
            .then((response: unknown) => {
                const typedResponse = response as { activity?: ActivityLocal };
                if (typedResponse.activity) {
                    setActivity(typedResponse.activity);
                    onSave(typedResponse.activity);
                }
            })
            .catch(() => {})
            .finally(() => {});
    }

    function recoverActivity(): void {
        Put(`/activities/${activity.id}/recover`).then((response: unknown) => {
            const typedResponse = response as { activity?: ActivityLocal };
            if (typedResponse.activity) {
                setActivity(typedResponse.activity);
                onSave(typedResponse.activity);
            }
        });
    }

    const isPreenrollmentBlockVisible =
        isRenewalPreenrollmentVisible() && !!activity.preenrollmentAllowed;
    const precedingActivityLabel = activity.originalClass
        ? `${activity.originalClass.name} · #${activity.originalClass.id}`
        : '-';

    return (
        <Grid container spacing={3}>
            {/* Left column - Form controls */}
            <Grid size={{ xs: 12, lg: activity.id ? 9 : 12 }}>
                <Grid container spacing={3}>
                    {/* Row 1: ID and creation date only */}
                    {activity.id && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttLabelForm
                                title={t('Code')}
                                text={`#${activity.id}`}
                            />
                        </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: activity.id ? 6 : 12 }}>
                        <SttLabelForm
                            title={t('Created')}
                            text={new DateBuilder(
                                activity.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy()}
                        />
                    </Grid>

                    {/* Row 2: Sport, status, and type */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttSportSelector
                            fullWidth
                            defVal={activity.sport?.name ?? undefined}
                            onChange={(sport: { id: number; name?: string }) =>
                                setSport(sport as SportLocal)
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttFormSelect
                            name="status"
                            caption={t('Status')}
                            defVal={activity.status}
                            options={[
                                {
                                    caption: t(`CS.OPEN`),
                                    value: 'OPEN',
                                },
                                {
                                    caption: t(`CS.CLOSE`),
                                    value: 'CLOSE',
                                },
                            ]}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttFormSelect
                            name="type"
                            caption={t('Type')}
                            defVal={activity.type}
                            options={[
                                {
                                    caption: t(`CT.COURSE`),
                                    value: 'COURSE',
                                },
                                {
                                    caption: t(`CT.PUNCTUAL`),
                                    value: 'PUNCTUAL',
                                },
                            ]}
                            onChange={setProperty}
                        />
                    </Grid>

                    {/* Row 3: Name (full width) */}
                    <Grid size={{ xs: 12 }}>
                        <SttFormText
                            caption={t('Name')}
                            name="name"
                            defVal={activity.name}
                            onChange={setProperty}
                        />
                    </Grid>

                    {/* Row 4: Description (full width) */}
                    <Grid size={{ xs: 12 }}>
                        <SttFormTextarea
                            caption={t('Description')}
                            name="description"
                            defVal={activity.description}
                            rows={6}
                            onChange={setProperty}
                        />
                    </Grid>

                    {/* Row 5: Professor and max students */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttSelectTeacher
                            fullWidth
                            teachers={teachers}
                            value={activity.teacher?.id ?? null}
                            onChange={(teacher) =>
                                setTeacher(teacher?.id ?? null)
                            }
                            cleanable
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormText
                            name="maxStudents"
                            caption={t('sc.class.main.maxStudents')}
                            defVal={
                                activity.maxStudents?.toString() ?? undefined
                            }
                            onChange={setProperty}
                        />
                    </Grid>

                    {/* Row 6: Inscription dates/times (full width) */}
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <SttFormDate
                            caption={t('sc.class.main.signupIni')}
                            name="inscIni"
                            defVal={activity.inscIni}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setInscDate(name, value as string);
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <SttFormTime
                            name="inscIni"
                            defVal={
                                activity.inscIni
                                    ? format(
                                          parseISO(activity.inscIni),
                                          'HH:mm',
                                      )
                                    : ''
                            }
                            min="00:00:00"
                            max="23:45"
                            interval={15}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: string;
                            }) => {
                                if (!name) return;
                                setInscTime(name, value);
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <SttFormDate
                            caption={t('sc.class.main.signupEnd')}
                            name="inscEnd"
                            defVal={activity.inscEnd}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setInscDate(name, value as string);
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <SttFormTime
                            name="inscEnd"
                            defVal={
                                activity.inscEnd
                                    ? format(
                                          parseISO(activity.inscEnd),
                                          'HH:mm',
                                      )
                                    : ''
                            }
                            min="00:00:00"
                            max="23:59:00"
                            interval={15}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: string;
                            }) => {
                                if (!name) return;
                                setInscTime(name, value);
                            }}
                            timeRangeOptions={{ includeMaxTime: true }}
                        />
                    </Grid>

                    {/* Row 7: Duration start and end (full width) */}
                    <Grid size={{ xs: 12 }}>
                        <SttFormPeriod
                            captionIni={t('DurationFrom')}
                            nameIni="ini"
                            defValIni={activity.ini}
                            nameEnd="end"
                            defValEnd={activity.end}
                            onChange={setProperty}
                        />
                    </Grid>

                    {/* A relocation copy names its origin; the pre-enrollment block already does */}
                    {!!activity.relocateStudents &&
                        !isPreenrollmentBlockVisible && (
                            <Grid size={{ xs: 12 }}>
                                <SttLabelForm
                                    title={t(
                                        'activity.preenrollment.originalActivity',
                                    )}
                                    text={precedingActivityLabel}
                                />
                            </Grid>
                        )}

                    {/* Renewal pre-enrollment: only for copies duplicated with the option
                        armed, and not in production while product validates it */}
                    {isPreenrollmentBlockVisible && (
                        <Grid size={{ xs: 12 }}>
                            <div className="rounded-lg border border-gray-300 p-4">
                                <Typography variant="subtitle1">
                                    {t('activity.preenrollment.title')}
                                </Typography>
                                <FieldHelpText i18nKey="activity.preenrollment.help" />

                                <div className="mt-4">
                                    <SttLabelForm
                                        title={t(
                                            'activity.preenrollment.originalActivity',
                                        )}
                                        text={precedingActivityLabel}
                                    />
                                </div>

                                <Grid container spacing={2} className="mt-3">
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <SttFormDate
                                            caption={t(
                                                'activity.preenrollment.signupIni',
                                            )}
                                            name="preenrollmentIni"
                                            defVal={activity.preenrollmentIni}
                                            onChange={({
                                                name,
                                                value,
                                            }: {
                                                name: string | undefined;
                                                value: unknown;
                                            }) => {
                                                if (!name) return;
                                                setPreenrollmentDate(
                                                    name,
                                                    value,
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <SttFormTime
                                            name="preenrollmentIni"
                                            defVal={safeFormatDate(
                                                activity.preenrollmentIni,
                                                'HH:mm',
                                            )}
                                            disabled={
                                                !activity.preenrollmentIni
                                            }
                                            min="00:00:00"
                                            max="23:45"
                                            interval={15}
                                            onChange={({
                                                name,
                                                value,
                                            }: {
                                                name: string | undefined;
                                                value: string;
                                            }) => {
                                                if (!name) return;
                                                setPreenrollmentTime(
                                                    name,
                                                    value,
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <SttFormDate
                                            caption={t(
                                                'activity.preenrollment.signupEnd',
                                            )}
                                            name="preenrollmentEnd"
                                            defVal={activity.preenrollmentEnd}
                                            onChange={({
                                                name,
                                                value,
                                            }: {
                                                name: string | undefined;
                                                value: unknown;
                                            }) => {
                                                if (!name) return;
                                                setPreenrollmentDate(
                                                    name,
                                                    value,
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <SttFormTime
                                            name="preenrollmentEnd"
                                            defVal={safeFormatDate(
                                                activity.preenrollmentEnd,
                                                'HH:mm',
                                            )}
                                            disabled={
                                                !activity.preenrollmentEnd
                                            }
                                            min="00:00:00"
                                            max="23:59:00"
                                            interval={15}
                                            onChange={({
                                                name,
                                                value,
                                            }: {
                                                name: string | undefined;
                                                value: string;
                                            }) => {
                                                if (!name) return;
                                                setPreenrollmentTime(
                                                    name,
                                                    value,
                                                );
                                            }}
                                            timeRangeOptions={{
                                                includeMaxTime: true,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12 }}>
                        <SttFormCheck
                            name="showSignedUsers"
                            caption={t('page.acitvity.showSignedUsers')}
                            checked={activity.showSignedUsers}
                            onChange={setProperty}
                        />
                    </Grid>
                </Grid>
            </Grid>

            {/* Right column - Tags */}
            {activity.id && (
                <Grid size={{ xs: 12, lg: 3 }}>
                    <SttTagsList
                        activity={{
                            id: activity.id,
                            tags: activity.tags as
                                | Array<{ id: number; name: string }>
                                | undefined,
                        }}
                        onUpdateActivityTags={(
                            tags?: Array<{ id: number; name: string }>,
                        ) => updateActivityTags(tags ?? [])}
                    />
                </Grid>
            )}

            {/* Buttons row */}
            <Grid size={{ xs: 12 }}>
                <div className="flex flex-wrap gap-3 items-center justify-start">
                    {/* All actions - left aligned */}
                    {!!activity.id && !activity.trash && (
                        <>
                            <SttButton
                                caption={t('Duplicate')}
                                icon={<CameraAltOutlined fontSize="small" />}
                                onClick={duplicate}
                            />
                            <SttButton
                                caption={t('Assistences')}
                                icon={<CheckCircleOutline fontSize="small" />}
                                onClick={() => setDialog(true)}
                            />
                            <SttButton
                                caption={t('GoToActivity')}
                                icon={<OpenInNewOutlined fontSize="small" />}
                                onClick={() =>
                                    window.open(
                                        `https://play.sporttia.com/center/${cxt?.sc?.id}/activity/${activity.id}`,
                                        '_blank',
                                    )
                                }
                            />
                        </>
                    )}

                    {/* Primary actions */}
                    {!!activity.id && !activity.trash && (
                        <SttButtonDelete
                            loading={deleteLoading}
                            onClick={deleteActivity}
                            permission={PERMISSIONS.ACTIVITY.DELETE}
                        />
                    )}
                    {!activity.trash && (
                        <SttButtonSave
                            loading={saveLoading}
                            onClick={saveActivity}
                            permission={
                                activity.id
                                    ? PERMISSIONS.ACTIVITY.MODIFY
                                    : PERMISSIONS.ACTIVITY.CREATE
                            }
                        />
                    )}
                    {!!activity.trash && (
                        <SttButtonRecover
                            onClick={recoverActivity}
                            permission={PERMISSIONS.ACTIVITY.DELETE}
                        />
                    )}
                </div>
            </Grid>
            <AttendancesDialog
                activity={
                    item as import('../../../types/models/Activity').Activity
                }
                open={dialog}
                onClose={() => setDialog(false)}
            />
        </Grid>
    );
}
